import { useState } from 'react';
import { Segmented } from 'antd';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { miktarMetni, oranMetni } from '../satis-analiz/bicim';
import { Bilgi } from '../satis-analiz/Bilgi';
import { BlokIskeleti } from '../satis-analiz/BlokIskeleti';
import { TiklaAc } from '../satis-analiz/TiklaAc';
import type { Donem } from '../satis-analiz/donem';
import type { StokKaynaklari } from './ekranKaynaklari';
import { useStokSorgu, type StokKaynak, type StokSorguFiltresi } from './useStokVerisi';
import {
  donusGunu, kategoriyeTopla, stokGunAyir, type StokGunSatiri, type SubeFarkSatiri,
} from './toplama';
import { kategoriNormalize } from './kategori';
import { kapsamNotuGorunsunMu } from './kapsam';
import { gunMetni } from './bicimYardimcilari';
import { KartHatasi } from './KartHatasi';
import type { StokDetay } from './detay/stokDetayTipleri';

interface Props {
  kaynaklar: StokKaynaklari;
  donem: Donem;
  sube?: string | null;
  ac?: (d: StokDetay) => void;
}

/** Kartlardaki "tümünü göster" düğmesi: listenin tamamı modalda açılır. */
function TumunuGoster({ testid, adet, onClick }: { testid: string; adet: number; onClick: () => void }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="focus-ring mt-2 self-start text-[12px] font-medium text-ink-700 hover:underline underline-offset-2"
    >
      Tümünü göster ({fmtNum(adet)})
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 1: Stok kaç günde dönüyor (990048). Grup = Σort. stok / Σsatılan maliyet × dönem günü.
// S352: çarpan ("x18") yerine gün gösterilir; sayı dönem uzunluğundan bağımsızdır. Grup tıklanınca ürünler.
// ════════════════════════════════════════════════════════════════════════════════════════════

export interface DevirSatiri {
  stok_kod: string;
  stok_ad: string;
  kategori: string;
  ort_envanter_deger: number;
  donem_cogs: number;
  devir_hizi: number;
  etkin_gun: number;
}

const DEVIR_BILGI =
  'Ortalama stok değeri ÷ günlük satılan malın maliyeti. Dönemde satışı olmayan ürünler bu listede yoktur '
  + '(onlar Ölü stok kartındadır); ortalama stoğu sıfır ya da eksi görünen ürünler de hesaba girmez.';

function DevirKarti({
  kaynak, f, ac,
}: {
  kaynak: StokKaynak | undefined;
  f: StokSorguFiltresi;
  ac?: (d: StokDetay) => void;
}) {
  const q = useStokSorgu<DevirSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="akis-devir-iskelet" baslik="Stok kaç günde dönüyor" yukseklik={200} satir={4} />;
  }
  if (q.isError) {
    return (
      <KartHatasi testid="akis-devir-hata" baslik="Stok kaç günde dönüyor" yukseklik={200} mesaj="Stok dönüş süresi yüklenemedi." />
    );
  }
  if (q.data === undefined) return null;
  const satirlar = q.data.data.map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));
  if (satirlar.length === 0) return null;

  const etkinGun = Number(satirlar[0].etkin_gun) || 0;
  const kategoriler = kategoriyeTopla(satirlar, 'donem_cogs', 'ort_envanter_deger')
    .map((k) => ({ kategori: k.kategori, gun: donusGunu(etkinGun, k.pay, k.payda) }))
    .filter((k): k is { kategori: string; gun: number } => k.gun !== null)
    .sort((a, b) => a.gun - b.gun);
  if (kategoriler.length === 0) return null;

  return (
    <div data-testid="akis-devir" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Stok kaç günde dönüyor"
        subtitle="Ürün grubuna göre; hızlı dönenden yavaşa"
        actions={<Bilgi testid="akis-devir-bilgi" metin={DEVIR_BILGI} />}
      >
        <ul className="list-none flex flex-col gap-1.5">
          {kategoriler.map((k) => {
            const icerik = (
              <>
                <span className="text-ink-900 truncate min-w-0">{k.kategori}</span>
                <span className="text-ink-700 tnum shrink-0">{gunMetni(k.gun)}</span>
              </>
            );
            const testid = `akis-devir-satir-${k.kategori}`;
            return (
              <li key={k.kategori}>
                {ac ? (
                  <button
                    type="button"
                    data-testid={testid}
                    onClick={() => ac({ tur: 'devir', kategori: k.kategori })}
                    className="focus-ring w-full flex items-center justify-between gap-3 text-[13px] hover:underline underline-offset-2 text-left"
                  >
                    {icerik}
                  </button>
                ) : (
                  <div data-testid={testid} className="flex items-center justify-between gap-3 text-[13px]">
                    {icerik}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 2: Sell-through (990046). Grup = Σsatılan / Σalınan
// ════════════════════════════════════════════════════════════════════════════════════════════

interface SellThroughSatiri {
  stok_kod: string;
  stok_ad: string;
  kategori: string;
  alinan: number;
  satilan: number;
  erime_oran: number | null;
}

const SELLTHROUGH_BILGI = "%100'ün üstü, dönemde stok eritildiği (satılan > alınan) anlamına gelir.";

function SellThroughKarti({ kaynak, f }: { kaynak: StokKaynak | undefined; f: StokSorguFiltresi }) {
  const q = useStokSorgu<SellThroughSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="akis-sellthrough-iskelet" baslik="Sell-through Oranı" yukseklik={200} satir={4} />;
  }
  if (q.isError) {
    return (
      <KartHatasi testid="akis-sellthrough-hata" baslik="Sell-through Oranı" yukseklik={200} mesaj="Sell-through oranı yüklenemedi." />
    );
  }
  if (q.data === undefined) return null;
  const satirlar = q.data.data.map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));
  if (satirlar.length === 0) return null;

  const kategoriler = kategoriyeTopla(satirlar, 'satilan', 'alinan')
    .filter((k): k is typeof k & { oran: number } => k.oran !== null)
    .sort((a, b) => b.oran - a.oran);
  if (kategoriler.length === 0) return null;

  return (
    <div data-testid="akis-sellthrough" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Sell-through Oranı"
        subtitle="Kategoriye göre — dönemde satılan / alınan"
        actions={<Bilgi testid="akis-sellthrough-bilgi" metin={SELLTHROUGH_BILGI} />}
      >
        <ul className="flex flex-col gap-1.5">
          {kategoriler.map((k) => (
            <li
              key={k.kategori}
              data-testid={`akis-sellthrough-satir-${k.kategori}`}
              className="flex items-center justify-between gap-3 text-[13px]"
            >
              <span className="text-ink-900 truncate min-w-0">{k.kategori}</span>
              <span className="text-ink-700 tnum shrink-0">{oranMetni(k.oran * 100, 2)}</span>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 3: Stok gün sayısı (990047), TiklaAc ile tembel. İki uçtan 10 ürün + "tümünü göster".
// S352: stoğu sıfır ya da eksi görünen ürünler iki listeye KARIŞMAZ (eksi gün "yakında bitecek"
// listesinin başını tutuyordu); ayrı sayılır ve istenirse modalda açılır.
// ════════════════════════════════════════════════════════════════════════════════════════════

const STOKGUN_BILGI =
  'Kalan gün = eldeki stok ÷ dönemdeki günlük ortalama satış. Stok tarihçesi 2026-04-15\'te başlar; '
  + 'daha eski dönem seçilirse gün sayısı olduğundan büyük çıkar.';
const STOKGUN_ADET = 10;

function StokGunTablo({
  testid, baslik, satirlar,
}: {
  testid: string;
  baslik: string;
  satirlar: { stok_kod: string; stok_ad: string; kalan_gun: number }[];
}) {
  return (
    <>
      <h3 className="text-[12px] font-medium text-ink-500 mb-1.5">{baslik}</h3>
      <table data-testid={testid} className="w-full text-[12.5px] border-collapse">
        <thead>
          <tr className="text-ink-400 text-left">
            <th className="font-normal py-1">Ürün</th>
            <th className="font-normal py-1 text-right">Kalan gün</th>
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s) => (
            <tr key={s.stok_kod} data-testid={`${testid}-${s.stok_kod}`} className="border-t border-border">
              <td className="py-1 pr-2 text-ink-800 truncate max-w-0 w-full">{s.stok_ad}</td>
              <td className="py-1 text-right tnum text-ink-700 whitespace-nowrap">{miktarMetni(s.kalan_gun)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function StokGunIcerik({
  kaynak, f, ac,
}: {
  kaynak: StokKaynak;
  f: StokSorguFiltresi;
  ac?: (d: StokDetay) => void;
}) {
  const q = useStokSorgu<StokGunSatiri>(kaynak, f);
  if (q.isLoading) {
    return <BlokIskeleti testid="akis-stokgun-iskelet" yukseklik={220} satir={6} />;
  }
  if (q.isError) {
    return <KartHatasi testid="akis-stokgun-hata" yukseklik={220} mesaj="Stok gün sayısı yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const { bitecek, asiri, eksi } = stokGunAyir(q.data.data);
  if (bitecek.length === 0 && eksi.length === 0) return null;
  // Karttaki iki kısa liste ÇAKIŞMAZ: ürün sayısı 2N'den azsa her liste en fazla yarısını alır
  // (tek ürün varsa yalnız "yakında bitecek"te görünür). Listenin tamamı modaldadır.
  const adet = bitecek.length <= 1 ? bitecek.length : Math.min(STOKGUN_ADET, Math.floor(bitecek.length / 2));
  const asiriAdet = bitecek.length <= 1 ? 0 : adet;

  return (
    <div className="p-5 flex flex-col gap-4">
      {bitecek.length > 0 && (
        <div className="flex flex-col sm:flex-row gap-5">
          <div className="flex-1 min-w-0 flex flex-col">
            <StokGunTablo testid="akis-stokgun-bitecek" baslik="Yakında bitecek" satirlar={bitecek.slice(0, adet)} />
            {ac && (
              <TumunuGoster testid="akis-stokgun-bitecek-tumu" adet={bitecek.length} onClick={() => ac({ tur: 'stokGun', uc: 'bitecek' })} />
            )}
          </div>
          <div className="flex-1 min-w-0 flex flex-col">
            <StokGunTablo testid="akis-stokgun-asiri" baslik="Aşırı stok" satirlar={asiri.slice(0, asiriAdet)} />
            {ac && (
              <TumunuGoster testid="akis-stokgun-asiri-tumu" adet={asiri.length} onClick={() => ac({ tur: 'stokGun', uc: 'asiri' })} />
            )}
          </div>
        </div>
      )}
      {eksi.length > 0 && (
        <p data-testid="akis-stokgun-eksi" className="text-[12px] text-ink-500">
          Dönemde satışı olup stoğu sıfır ya da eksi görünen {fmtNum(eksi.length)} ürün bu listelerde yok
          (stok kaydı eksik ya da ürün bitmiş).{' '}
          {ac && (
            <button
              type="button"
              data-testid="akis-stokgun-eksi-goster"
              onClick={() => ac({ tur: 'stokGun', uc: 'eksi' })}
              className="focus-ring font-medium text-ink-700 hover:underline underline-offset-2"
            >
              Göster
            </button>
          )}
        </p>
      )}
    </div>
  );
}

function StokGunKarti({
  kaynak, f, ac,
}: {
  kaynak: StokKaynak | undefined;
  f: StokSorguFiltresi;
  ac?: (d: StokDetay) => void;
}) {
  if (!kaynak) return null;
  return (
    <div data-testid="akis-stokgun" className="h-full">
      <SectionCard className="h-full flex flex-col" bodyClass="p-0 flex-1 flex flex-col">
        <TiklaAc
          anahtar="akis-stokgun"
          baslik="Stok gün sayısı"
          altBaslik="Eldeki stok kaç gün yeter: yakında bitecek / aşırı stok"
          bilgi={<Bilgi testid="akis-stokgun-bilgi" metin={STOKGUN_BILGI} />}
        >
          <StokGunIcerik kaynak={kaynak} f={f} ac={ac} />
        </TiklaAc>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 4: Şubeler arası ürün farkı (990078), TiklaAc ile tembel (sorgu 3-6 sn).
// Şube seçilir; dönemde YALNIZ o şubede satılmış ürünler listelenir. Stok kolonları kayıtlı şube
// stoğudur ve bilgi amaçlıdır (transfersiz taşınan malda yanlıştır).
// ════════════════════════════════════════════════════════════════════════════════════════════

const SUBEFARK_BILGI =
  'Seçili dönemde yalnız bu şubede satılmış, öbür şubede hiç satılmamış ürünler. Stok kolonları kayıtlı '
  + 'şube stoğudur; şubeler arası transfer fişi kesilmeden taşınan malda yanlış olabilir.';
const SUBEFARK_ADET = 10;

export function subeFarkSubeleri(satirlar: SubeFarkSatiri[]): string[] {
  return [...new Set(satirlar.map((s) => s.sube))].sort((a, b) => a.localeCompare(b, 'tr'));
}

function SubeFarkIcerik({
  kaynak, f, sayfaSubesi, ac,
}: {
  kaynak: StokKaynak;
  f: StokSorguFiltresi;
  sayfaSubesi: string | null;
  ac?: (d: StokDetay) => void;
}) {
  // Şube süzgeci bu ds'e GİTMEZ (ds tüm şubeleri tek seferde döndürür); seçim kartın içindedir.
  const q = useStokSorgu<SubeFarkSatiri>(kaynak, { ...f, sube: null });
  const [secim, setSecim] = useState<string | null>(null);
  if (q.isLoading) {
    return <BlokIskeleti testid="akis-subefark-iskelet" yukseklik={220} satir={6} />;
  }
  if (q.isError) {
    return <KartHatasi testid="akis-subefark-hata" yukseklik={220} mesaj="Şubeler arası ürün farkı yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const tum = q.data.data;
  const subeler = subeFarkSubeleri(tum);
  if (subeler.length === 0) {
    return <p data-testid="akis-subefark-bos" className="p-5 text-[13px] text-ink-500">Dönemde yalnız tek şubede satılan ürün yok.</p>;
  }
  // Seçim yapılmadıysa sayfanın şube süzgeci (listedeyse), o da yoksa ilk şube.
  const secili = secim !== null && subeler.includes(secim)
    ? secim
    : (sayfaSubesi !== null && subeler.includes(sayfaSubesi) ? sayfaSubesi : subeler[0]);
  const satirlar = tum
    .filter((s) => s.sube === secili)
    .sort((a, b) => (Number(b.satis_tutar) || 0) - (Number(a.satis_tutar) || 0));
  const toplam = satirlar.reduce((t, s) => t + (Number(s.satis_tutar) || 0), 0);

  return (
    <div className="p-5 flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div data-testid="akis-subefark-secici">
          <Segmented
            size="small"
            value={secili}
            onChange={(v) => setSecim(String(v))}
            options={subeler.map((s) => ({ value: s, label: s }))}
          />
        </div>
        <p data-testid="akis-subefark-ozet" className="text-[13px] text-ink-700 tnum">
          Yalnız <span className="font-semibold text-ink-900">{secili}</span> şubesinde satılan{' '}
          <span className="font-semibold text-ink-900">{fmtNum(satirlar.length)} ürün</span> · {fmtTRY(toplam)}
        </p>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="akis-subefark-tablo" className="w-full text-[12.5px] border-collapse">
          <thead>
            <tr className="text-ink-400 text-left">
              <th className="font-normal py-1">Ürün</th>
              <th className="font-normal py-1 px-2 text-right">Satış</th>
              <th className="font-normal py-1 px-2 text-right whitespace-nowrap">Bu şubede stok</th>
              <th className="font-normal py-1 pl-2 text-right whitespace-nowrap">Öbür şubede stok</th>
            </tr>
          </thead>
          <tbody>
            {satirlar.slice(0, SUBEFARK_ADET).map((s) => (
              <tr key={s.stok_kod} data-testid={`akis-subefark-${s.stok_kod}`} className="border-t border-border">
                <td className="py-1 pr-2 text-ink-800">{s.stok_ad}</td>
                <td className="py-1 px-2 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(Number(s.satis_tutar) || 0)}</td>
                <td className="py-1 px-2 text-right tnum text-ink-700 whitespace-nowrap">{miktarMetni(Number(s.sube_stok) || 0)}</td>
                <td className="py-1 pl-2 text-right tnum text-ink-700 whitespace-nowrap">{miktarMetni(Number(s.diger_stok) || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {ac && satirlar.length > 0 && (
        <TumunuGoster testid="akis-subefark-tumu" adet={satirlar.length} onClick={() => ac({ tur: 'subeFark', sube: secili })} />
      )}
    </div>
  );
}

function SubeFarkKarti({
  kaynak, f, sayfaSubesi, ac,
}: {
  kaynak: StokKaynak | undefined;
  f: StokSorguFiltresi;
  sayfaSubesi: string | null;
  ac?: (d: StokDetay) => void;
}) {
  if (!kaynak) return null;
  return (
    <div data-testid="akis-subefark" className="h-full">
      <SectionCard className="h-full flex flex-col" bodyClass="p-0 flex-1 flex flex-col">
        <TiklaAc
          anahtar="akis-subefark"
          baslik="Şubeler arası ürün farkı"
          altBaslik="Bir şubede satılıp öbüründe satılmayan ürünler"
          bilgi={<Bilgi testid="akis-subefark-bilgi" metin={SUBEFARK_BILGI} />}
        >
          <SubeFarkIcerik kaynak={kaynak} f={f} sayfaSubesi={sayfaSubesi} ac={ac} />
        </TiklaAc>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Blok: AKIŞ
// ════════════════════════════════════════════════════════════════════════════════════════════

const KAPSAM_NOT_METNI = 'Şube süzgeci bu blokta geçerli değil — ölçüler tüm şubeleri kapsar.';

/**
 * Kapsam notu bloğun üç ds'inin (990046/990047/990048) GERÇEK `@LOKASYON` bildirimine bakar (`kapsam.ts`,
 * SermayeBlok'taki `sermaye-kapsam-not` ile AYNI mekanizma). Şube farkı kartı (990078) nota girmez:
 * şubeyi kendi seçicisiyle ayırır.
 */
export function AkisBlok({ kaynaklar, donem, sube = null, ac }: Props) {
  const f: StokSorguFiltresi = { donem, sube, kategori: null };

  const hicKaynakYok = !kaynaklar.devirHizi && !kaynaklar.sellThrough && !kaynaklar.stokGun && !kaynaklar.subeFark;
  if (hicKaynakYok) return null;

  const kapsamNotuGorunur = kapsamNotuGorunsunMu(
    [kaynaklar.devirHizi, kaynaklar.sellThrough, kaynaklar.stokGun],
    '@LOKASYON',
    sube,
  );

  return (
    <section data-testid="akis-blok" className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-[16px] font-semibold text-ink-900">
          Akış
          {kapsamNotuGorunur && (
            <span data-testid="akis-kapsam-not" className="block text-[11px] font-normal text-ink-400 mt-0.5">
              {KAPSAM_NOT_METNI}
            </span>
          )}
        </h2>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
        <DevirKarti kaynak={kaynaklar.devirHizi} f={f} ac={ac} />
        <SellThroughKarti kaynak={kaynaklar.sellThrough} f={f} />
      </div>
      {/* items-start: biri açılınca öbürünün kartı boş yere uzamasın. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
        <StokGunKarti kaynak={kaynaklar.stokGun} f={f} ac={ac} />
        <SubeFarkKarti kaynak={kaynaklar.subeFark} f={f} sayfaSubesi={sube} ac={ac} />
      </div>
    </section>
  );
}
